/**
 * TrialDrawer — the single-trial drawer (Story I).
 *
 * Clicking a board card (or opening a card deep link) opens this right-side,
 * dismissable panel without cluttering the board (design spec §"Single-trial
 * drawer", Principle 2). It is lazy by construction: it fetches `GET /trials/<id>`
 * only once it has a `trialId` to show, so a board that no one has drilled into
 * issues no trial reads.
 *
 * Three tabs frame it. **Overview** ships now — it is pure `trial_jobs` store-row
 * data: verdict/status, the failure class + message, lease facts, attempt count,
 * the four timestamps with per-stage dwell derived from them, and a blocked
 * trial's admission reason. **Timeline** (Story J) and **Diagnostics** (Story K)
 * are stubs here so those stories can fill them without reshaping the drawer.
 */

import { useEffect, useRef, useState } from 'react'
import { type ReadApiError, fetchDiagnostics, fetchObjectUrl, fetchTrajectory, fetchTrial } from './client'
import { formatClock, formatDuration, formatInstant, phaseDwellText, stageDwells } from './format'
import { type PhaseTimelineEntry, type TrajectoryStep, buildPhaseTimeline, parseTrajectory } from './timeline'
import type { PhaseEvent, TrialDetail, TrialDiagnostics, TrialRow } from './types'

/** Default cadence (ms) the in-flight Timeline re-polls for a new latest phase — within the spec's 2–5s band. */
export const DEFAULT_TIMELINE_POLL_MS = 3_000

/**
 * Friendly, render-ordered labels for the evidence object keys the manifest
 * exposes (mirrors `TrialBundleObjectRefs`). `bundle` is rendered separately as
 * the download-all, so it is not in this primary list.
 */
const EVIDENCE_LABELS: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'result', label: 'result.json' },
  { key: 'judgeScores', label: 'judge-scores.json' },
  { key: 'trajectory', label: 'trajectory.json' },
  { key: 'inputs', label: 'inputs.json' },
  { key: 'metadata', label: 'evidence-metadata.json' },
  { key: 'traces', label: 'traces.json' },
  { key: 'autobuildEvidence', label: 'autobuild-evidence.json' },
]

/** The three drawer tabs; Overview is the only one Story I fills. */
export type DrawerTab = 'overview' | 'timeline' | 'diagnostics'

interface TrialDrawerProps {
  /** The trial to show, or null when the drawer is closed (nothing is fetched). */
  trialId: string | null
  /** Dismiss the drawer (backdrop click, the close control, or Escape). */
  onClose: () => void
  /** Injected fetch for tests; defaults to the global. */
  fetchImpl?: typeof fetch
  /** Clock override for deterministic dwell rendering in tests. */
  now?: number
  /** In-flight Timeline poll cadence (ms); defaults to {@link DEFAULT_TIMELINE_POLL_MS}. Injected for tests. */
  timelinePollMs?: number
}

interface DrawerState {
  detail: TrialDetail | null
  error: { endpoint: string; message: string } | null
}

const INITIAL_STATE: DrawerState = { detail: null, error: null }

export function TrialDrawer({
  trialId,
  onClose,
  fetchImpl,
  now,
  timelinePollMs,
}: TrialDrawerProps): JSX.Element | null {
  const [state, setState] = useState<DrawerState>(INITIAL_STATE)
  const [tab, setTab] = useState<DrawerTab>('overview')
  const panelRef = useRef<HTMLElement>(null)

  // Lazy fetch: keyed on trialId, so the request fires only when the drawer
  // opens (trialId goes non-null) and re-fires when it opens onto another trial.
  // A null trialId means closed — no fetch, state reset so a reopen never flashes
  // the previous trial's data.
  useEffect(() => {
    if (trialId === null) {
      setState(INITIAL_STATE)
      return
    }
    let live = true
    setState(INITIAL_STATE)
    setTab('overview')
    fetchTrial(trialId, fetchImpl)
      .then((detail) => {
        if (live) {
          setState({ detail, error: null })
        }
      })
      .catch((err: ReadApiError) => {
        if (live) {
          setState({ detail: null, error: { endpoint: err.endpoint, message: err.message } })
        }
      })
    return () => {
      live = false
    }
  }, [trialId, fetchImpl])

  // Escape closes the drawer, and Tab/Shift+Tab cycle only within the panel. The
  // panel is `aria-modal`, which tells a screen reader everything behind it is
  // inert; without a focus trap a sighted keyboard user would still Tab straight
  // out to the board behind the backdrop, breaking that contract (Principle 3).
  // Keyed on `tab` too so the trap re-reads the focusable set after a tab switch
  // swaps the body content.
  useEffect(() => {
    if (trialId === null) {
      return
    }
    const focusables = (): HTMLElement[] =>
      Array.from(
        panelRef.current?.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        ) ?? []
      ).filter((el) => !el.hasAttribute('disabled'))

    focusables()[0]?.focus()

    const onKey = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        onClose()
        return
      }
      if (event.key !== 'Tab') {
        return
      }
      const items = focusables()
      if (items.length === 0) {
        return
      }
      const first = items[0]
      const last = items[items.length - 1]
      const active = panelRef.current?.ownerDocument.activeElement
      const wrapBackward = event.shiftKey && active === first
      const wrapForward = !event.shiftKey && active === last
      if (wrapBackward || wrapForward) {
        event.preventDefault()
        ;(event.shiftKey ? last : first).focus()
      }
    }
    globalThis.addEventListener?.('keydown', onKey)
    return () => globalThis.removeEventListener?.('keydown', onKey)
  }, [trialId, tab, onClose])

  if (trialId === null) {
    return null
  }

  return (
    <div className="drawer-root" data-testid="trial-drawer">
      {/* biome-ignore lint/a11y/useKeyWithClickEvents: Escape is handled globally above; the backdrop is a redundant pointer affordance. */}
      <div className="drawer-backdrop" data-testid="drawer-backdrop" onClick={onClose} />
      <aside ref={panelRef} className="drawer-panel" role="dialog" aria-label="trial detail" aria-modal="true">
        <header className="drawer-header">
          <span className="drawer-title tabular">{trialId}</span>
          <button type="button" className="drawer-close" aria-label="close" onClick={onClose}>
            ×
          </button>
        </header>
        <nav className="drawer-tabs" role="tablist">
          <TabButton tab="overview" active={tab} label="Overview" onSelect={setTab} />
          <TabButton tab="timeline" active={tab} label="Timeline" onSelect={setTab} />
          <TabButton tab="diagnostics" active={tab} label="Diagnostics" onSelect={setTab} />
        </nav>
        <div className="drawer-body">{renderBody(tab, state, now, fetchImpl, timelinePollMs)}</div>
      </aside>
    </div>
  )
}

interface TabButtonProps {
  tab: DrawerTab
  active: DrawerTab
  label: string
  onSelect: (tab: DrawerTab) => void
}

function TabButton({ tab, active, label, onSelect }: TabButtonProps): JSX.Element {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active === tab}
      className={active === tab ? 'drawer-tab drawer-tab-active' : 'drawer-tab'}
      data-testid={`tab-${tab}`}
      onClick={() => onSelect(tab)}
    >
      {label}
    </button>
  )
}

function renderBody(
  tab: DrawerTab,
  state: DrawerState,
  now: number | undefined,
  fetchImpl: typeof fetch | undefined,
  timelinePollMs: number | undefined
): JSX.Element {
  if (state.error) {
    return (
      <div className="state state-error" role="alert" data-testid="drawer-error">
        <p className="state-title">could not reach {state.error.endpoint}</p>
        <p className="state-detail">{state.error.message}</p>
      </div>
    )
  }
  if (state.detail === null) {
    return (
      <div className="state state-loading" data-testid="drawer-loading">
        <p className="state-title">loading trial…</p>
      </div>
    )
  }
  if (tab === 'timeline') {
    return <TimelineTab detail={state.detail} fetchImpl={fetchImpl} pollMs={timelinePollMs} now={now} />
  }
  if (tab === 'diagnostics') {
    return <DiagnosticsTab detail={state.detail} fetchImpl={fetchImpl} />
  }
  return <OverviewTab detail={state.detail} now={now} />
}

// ---------------------------------------------------------------------------
// Timeline tab (Story J)
// ---------------------------------------------------------------------------

/**
 * The Timeline tab. It is lazy by mount — it renders (and thus polls/fetches)
 * only once the Timeline tab is selected, so a drawer left on Overview issues no
 * timeline work. It branches on whether the trial is still in flight:
 *
 *  - IN-FLIGHT (`bundleRef === null`): the daemon does not retain the historical
 *    event stream — only the latest phase (Story E). So {@link LiveTimeline}
 *    re-polls `GET /trials/<id>` and appends each NEW latest phase as it arrives,
 *    a current-state-forward reconstruction (NOT a replay), labelled as such.
 *  - FINISHED: the full ordered step sequence lives in the bundle's
 *    `trajectory.json`, which {@link FinishedTimeline} resolves through Story K's
 *    object interface and renders.
 */
function TimelineTab({
  detail,
  fetchImpl,
  pollMs,
  now,
}: {
  detail: TrialDetail
  fetchImpl: typeof fetch | undefined
  pollMs: number | undefined
  now: number | undefined
}): JSX.Element {
  if (detail.trial.bundleRef === null) {
    return <LiveTimeline detail={detail} fetchImpl={fetchImpl} pollMs={pollMs ?? DEFAULT_TIMELINE_POLL_MS} now={now} />
  }
  return <FinishedTimeline detail={detail} fetchImpl={fetchImpl} now={now} />
}

/** The live-tail snapshot the in-flight timeline rebuilds from on each poll. */
interface LivePhaseState {
  events: PhaseEvent[]
  latestPhase: string | null
  phaseDwellMs: number | null
}

/**
 * In-flight timeline: the trial's durable phase history rendered as a
 * timestamped, dwell-annotated progression. The W5 daemon persists a durable,
 * ordered `phaseEvents` history (the seed-of-truth that survives a restart) plus
 * the live `latestPhase`/`phaseDwellMs` tail. On mount it renders from the first
 * fetch, then re-polls `GET /trials/<id>` on a relaxed interval and rebuilds from
 * the freshest snapshot via {@link buildPhaseTimeline} — so each new phase, its
 * wall-clock `when`, and the dwell between phases surface as they arrive. A trial
 * with no phase at all shows an honest waiting state, never a spinner. A poll
 * failure — or a transient snapshot with no phase data at all — is non-fatal: the
 * last good timeline is retained and polling continues, so a blip never wipes it.
 */
function LiveTimeline({
  detail,
  fetchImpl,
  pollMs,
  now,
}: {
  detail: TrialDetail
  fetchImpl: typeof fetch | undefined
  pollMs: number
  now: number | undefined
}): JSX.Element {
  const trialId = detail.trial.id
  const [live, setLive] = useState<LivePhaseState>(() => ({
    events: detail.phaseEvents,
    latestPhase: detail.latestPhase,
    phaseDwellMs: detail.phaseDwellMs,
  }))

  useEffect(() => {
    let alive = true
    const poll = async (): Promise<void> => {
      try {
        const fresh = await fetchTrial(trialId, fetchImpl)
        if (!alive) {
          return
        }
        setLive((prev) => {
          // A snapshot carrying no phase data at all is a transient read; keep the
          // last good timeline rather than collapsing to the empty state.
          if (fresh.phaseEvents.length === 0 && fresh.latestPhase === null && hasPhaseData(prev)) {
            return prev
          }
          return { events: fresh.phaseEvents, latestPhase: fresh.latestPhase, phaseDwellMs: fresh.phaseDwellMs }
        })
      } catch {
        // A transient read failure must not wipe the timeline; keep polling. The
        // drawer's own header surfaces a hard, persistent outage.
      }
    }
    const handle = setInterval(() => void poll(), pollMs)
    return () => {
      alive = false
      clearInterval(handle)
    }
  }, [trialId, fetchImpl, pollMs])

  const entries = buildPhaseTimeline(live.events, live.latestPhase, live.phaseDwellMs, now ?? Date.now())
  if (entries.length === 0) {
    // No phase retained — after a daemon restart with no durable history, or
    // before the first event. Honest waiting state, not a stuck spinner.
    return (
      <div className="state state-empty" data-testid="timeline-empty">
        <p className="state-detail">no phase reported yet — waiting for the trial's next event.</p>
      </div>
    )
  }
  return (
    <section className="trial-timeline" data-testid="timeline-live">
      <p className="timeline-mode" data-testid="timeline-mode">
        live · phase history (current-state-forward, not a replay)
      </p>
      <ol className="timeline-list">
        {entries.map((entry, index) => (
          <TimelinePhaseRow key={`${index}-${entry.phase}`} entry={entry} />
        ))}
      </ol>
    </section>
  )
}

/** Whether a live snapshot carries any phase data to render. */
function hasPhaseData(state: LivePhaseState): boolean {
  return state.events.length > 0 || state.latestPhase !== null
}

/** One row of the phase timeline: when (clock) · sub-label · dwell, tail marked `now`. */
function TimelinePhaseRow({ entry }: { entry: PhaseTimelineEntry }): JSX.Element {
  return (
    <li className={entry.active ? 'timeline-row timeline-row-active' : 'timeline-row'} data-testid="timeline-phase">
      <span className="timeline-phase-when tabular" title={formatInstant(entry.ts)}>
        {formatClock(entry.ts)}
      </span>
      <span className="timeline-phase-label" title={entry.phase}>
        {entry.label}
      </span>
      {entry.dwellMs !== null ? (
        <span className="timeline-phase-dwell tabular">{formatDuration(entry.dwellMs)}</span>
      ) : null}
      {entry.active ? <span className="timeline-phase-now">now</span> : null}
    </li>
  )
}

interface FinishedTimelineState {
  steps: TrajectoryStep[] | null
  /** True when the daemon returned 409: run not yet finalized (calm transient state). */
  runPending: boolean
  error: { endpoint: string; message: string } | null
}

const FINISHED_TIMELINE_INITIAL: FinishedTimelineState = { steps: null, runPending: false, error: null }

/**
 * Finished timeline: the `trajectory.json` step sequence, resolved through Story
 * K's object interface ({@link fetchTrajectory} — presigned URL then a direct S3
 * read, so the bytes never touch the daemon, Principle 1) and shaped by the
 * tolerant {@link parseTrajectory}. Lazy by mount; a resolution failure renders
 * an honest error state naming the source, and a finished-but-step-less document
 * renders an honest empty state — never a spinner.
 *
 * A 409 response from the daemon means the run manifest has not been written yet:
 * this trial is done but other trials in the run are still in flight, so
 * `trajectory.json` won't be assembled until the run finalizes. In this case we
 * fall back to the trial's durable `phaseEvents` history — the shim lifecycle
 * sequence that survived a daemon restart — so the operator sees real data instead
 * of only a holding message. When no phase events exist yet, we show an honest
 * explanatory message rather than an error.
 */
function FinishedTimeline({
  detail,
  fetchImpl,
  now,
}: {
  detail: TrialDetail
  fetchImpl: typeof fetch | undefined
  now: number | undefined
}): JSX.Element {
  const trialId = detail.trial.id
  const [state, setState] = useState<FinishedTimelineState>(FINISHED_TIMELINE_INITIAL)

  useEffect(() => {
    let live = true
    setState(FINISHED_TIMELINE_INITIAL)
    fetchTrajectory(trialId, fetchImpl)
      .then((raw) => {
        if (live) {
          setState({ steps: parseTrajectory(raw), runPending: false, error: null })
        }
      })
      .catch((err: ReadApiError) => {
        if (!live) {
          return
        }
        // 409: run not finalized yet — calm state, not an error.
        if (err.status === 409) {
          setState({ steps: null, runPending: true, error: null })
        } else {
          setState({ steps: null, runPending: false, error: { endpoint: err.endpoint, message: err.message } })
        }
      })
    return () => {
      live = false
    }
  }, [trialId, fetchImpl])

  if (state.runPending) {
    // This trial is done but the run hasn't finalized yet, so trajectory.json
    // isn't available. Fall back to the durable phase history if it exists —
    // it's real data the operator can use while waiting for the run to complete.
    // Suppress the active marker — buildPhaseTimeline marks the last entry as
    // active (for the "now" pill), but here the trial is already done; the last
    // phase is historical, not in-progress.
    const phaseEntries = buildPhaseTimeline(detail.phaseEvents, null, null, now ?? Date.now()).map(
      (entry) => ({ ...entry, active: false }),
    )
    if (phaseEntries.length > 0) {
      return (
        <section className="trial-timeline" data-testid="timeline-run-pending">
          <p className="timeline-mode" data-testid="timeline-mode">
            phase history · trial done, run not yet finalized
          </p>
          <ol className="timeline-list">
            {phaseEntries.map((entry, index) => (
              <TimelinePhaseRow key={`${index}-${entry.phase}`} entry={entry} />
            ))}
          </ol>
        </section>
      )
    }
    return (
      <div className="state state-empty" data-testid="timeline-run-pending">
        <p className="state-detail">
          this trial is done, but its run hasn&apos;t been finalized yet — trajectory.json will appear once all trials
          complete.
        </p>
      </div>
    )
  }
  if (state.error) {
    return (
      <div className="state state-error" role="alert" data-testid="timeline-error">
        <p className="state-title">could not load {state.error.endpoint}</p>
        <p className="state-detail">{state.error.message}</p>
      </div>
    )
  }
  if (state.steps === null) {
    return (
      <div className="state state-loading" data-testid="timeline-loading">
        <p className="state-title">loading trajectory…</p>
      </div>
    )
  }
  if (state.steps.length === 0) {
    return (
      <div className="state state-empty" data-testid="timeline-finished-empty">
        <p className="state-detail">no trajectory steps recorded for this trial.</p>
      </div>
    )
  }
  return (
    <section className="trial-timeline" data-testid="timeline-finished">
      <p className="timeline-mode" data-testid="timeline-mode">
        trajectory · {state.steps.length} step{state.steps.length === 1 ? '' : 's'}
      </p>
      <ol className="timeline-list">
        {state.steps.map((step) => (
          <TimelineStepRow key={step.index} step={step} />
        ))}
      </ol>
    </section>
  )
}

/** One trajectory step row: an ordinal, a short label, and the body when present. */
function TimelineStepRow({ step }: { step: TrajectoryStep }): JSX.Element {
  return (
    <li className="timeline-row timeline-step" data-testid="timeline-step">
      <span className="timeline-step-index tabular">{step.index + 1}</span>
      <span className="timeline-step-label">{step.label}</span>
      {step.detail !== null ? <span className="timeline-step-detail">{step.detail}</span> : null}
    </li>
  )
}

interface DiagnosticsState {
  links: TrialDiagnostics | null
  error: { endpoint: string; message: string } | null
}

const DIAGNOSTICS_INITIAL: DiagnosticsState = { links: null, error: null }

/**
 * The Diagnostics tab (Story K): a finished trial's S3 evidence links, resolved
 * from the run manifest. Lazy by mount — it only renders (and thus only fetches)
 * once the Diagnostics tab is selected, so a drawer left on Overview resolves no
 * manifest. An in-flight trial (`bundleRef === null`) has no bundle yet, so it
 * shows the fallback (the live phase + dwell from Story E/I) WITHOUT a fetch,
 * never a broken link. Each evidence link is an anchor to the daemon's object
 * endpoint, which 302-redirects to a short-lived presigned S3 URL — the bytes
 * never stream through the daemon (Principle 1).
 */
function DiagnosticsTab({
  detail,
  fetchImpl,
}: {
  detail: TrialDetail
  fetchImpl: typeof fetch | undefined
}): JSX.Element {
  const { trial } = detail
  const inFlight = trial.bundleRef === null
  const [state, setState] = useState<DiagnosticsState>(DIAGNOSTICS_INITIAL)

  // Resolve the link map on open — but only for a finished trial. An in-flight
  // trial has no bundle, so we skip the fetch entirely and render the fallback.
  useEffect(() => {
    if (inFlight) {
      return
    }
    let live = true
    setState(DIAGNOSTICS_INITIAL)
    fetchDiagnostics(trial.id, fetchImpl)
      .then((links) => {
        if (live) {
          setState({ links, error: null })
        }
      })
      .catch((err: ReadApiError) => {
        if (live) {
          setState({ links: null, error: { endpoint: err.endpoint, message: err.message } })
        }
      })
    return () => {
      live = false
    }
  }, [trial.id, inFlight, fetchImpl])

  if (inFlight) {
    return <DiagnosticsFallback detail={detail} />
  }
  if (state.error) {
    return (
      <div className="state state-error" role="alert" data-testid="diagnostics-error">
        <p className="state-title">could not reach {state.error.endpoint}</p>
        <p className="state-detail">{state.error.message}</p>
      </div>
    )
  }
  if (state.links === null) {
    return (
      <div className="state state-loading" data-testid="diagnostics-loading">
        <p className="state-title">resolving evidence…</p>
      </div>
    )
  }
  // Finished, but the run is not finalized yet (manifest absent) — honest empty
  // state, never a dead link.
  if (state.links.objects === null) {
    return (
      <div className="state state-empty" data-testid="diagnostics-pending">
        <p className="state-detail">evidence bundle not available yet — the run has not been finalized.</p>
      </div>
    )
  }
  return <DiagnosticsLinks trialId={detail.trial.id} links={state.links} fetchImpl={fetchImpl} />
}

/**
 * Open one evidence object. The link `href` points at the daemon's object
 * endpoint, which returns a JSON body (the presigned S3 URL) rather than a
 * redirect a browser navigation could follow directly. So we intercept EVERY
 * gesture (primary click, Cmd/Ctrl/Shift+click, and middle-click), resolve the
 * presigned S3 URL over `fetch`, then open THAT directly — the bytes flow
 * browser→S3, never through the daemon (Principle 1). `href` is kept only so
 * the link is a real anchor (focusable, styled); it is never actually followed.
 * A failure is surfaced via `onError`, never swallowed.
 */
function openEvidenceObject(
  event: React.MouseEvent<HTMLAnchorElement>,
  trialId: string,
  key: string,
  fetchImpl: typeof fetch | undefined,
  onError: (key: string, message: string) => void
): void {
  if (event.defaultPrevented) {
    return
  }
  // The href is not a public URL — a bare navigation would hit the JSON
  // endpoint and render raw JSON instead of opening the object. Intercept
  // unconditionally.
  event.preventDefault()
  fetchObjectUrl(trialId, key, fetchImpl)
    .then((url) => {
      window.open(url, '_blank', 'noreferrer')
    })
    .catch((err: ReadApiError) => {
      // Surface the failure on the specific link rather than dropping it — a dead
      // click with no feedback is undiagnosable (503, timeout, expired key).
      onError(key, err.message)
    })
}

/** The resolved evidence link list plus the download-all anchor. */
function DiagnosticsLinks({
  trialId,
  links,
  fetchImpl,
}: {
  trialId: string
  links: TrialDiagnostics
  fetchImpl: typeof fetch | undefined
}): JSX.Element {
  const objects = links.objects ?? {}
  const present = EVIDENCE_LABELS.filter((entry) => objects[entry.key] !== undefined)
  const hasBundle = objects.bundle !== undefined
  const [linkErrors, setLinkErrors] = useState<Record<string, string>>({})
  const onError = (key: string, message: string): void => {
    setLinkErrors((prev) => ({ ...prev, [key]: message }))
  }
  return (
    <section className="trial-diagnostics" data-testid="diagnostics-tab">
      <ul className="diagnostics-list">
        {present.map((entry) => (
          <li key={entry.key}>
            <a
              className="diagnostics-link"
              href={objects[entry.key]}
              data-testid={`diagnostics-link-${entry.key}`}
              target="_blank"
              rel="noreferrer"
              onClick={(e) => openEvidenceObject(e, trialId, entry.key, fetchImpl, onError)}
              onAuxClick={(e) => openEvidenceObject(e, trialId, entry.key, fetchImpl, onError)}
            >
              {entry.label}
            </a>
            {linkErrors[entry.key] !== undefined ? (
              <span
                className="diagnostics-link-error"
                role="alert"
                data-testid={`diagnostics-link-error-${entry.key}`}
                title={linkErrors[entry.key]}
              >
                {' '}
                ⚠ could not open
              </span>
            ) : null}
          </li>
        ))}
      </ul>
      {hasBundle ? (
        <span className="diagnostics-download-all-wrap">
          <a
            className="diagnostics-download-all"
            href={objects.bundle}
            data-testid="diagnostics-download-all"
            target="_blank"
            rel="noreferrer"
            onClick={(e) => openEvidenceObject(e, trialId, 'bundle', fetchImpl, onError)}
            onAuxClick={(e) => openEvidenceObject(e, trialId, 'bundle', fetchImpl, onError)}
          >
            download all (bundle.tar.gz)
          </a>
          {linkErrors.bundle !== undefined ? (
            <span
              className="diagnostics-link-error"
              role="alert"
              data-testid="diagnostics-link-error-bundle"
              title={linkErrors.bundle}
            >
              {' '}
              ⚠ could not open
            </span>
          ) : null}
        </span>
      ) : null}
      {links.manifestUri !== null ? (
        <p className="diagnostics-manifest tabular" data-testid="diagnostics-manifest">
          {links.manifestUri}
        </p>
      ) : null}
    </section>
  )
}

/** In-flight fallback: no bundle yet, so show the live phase + dwell, not a broken link. */
function DiagnosticsFallback({ detail }: { detail: TrialDetail }): JSX.Element {
  const { latestPhase, phaseDwellMs } = detail
  return (
    <div className="state state-empty" data-testid="diagnostics-fallback">
      <p className="state-detail">no evidence bundle yet — this trial is still in flight.</p>
      {latestPhase !== null ? (
        <p className="diagnostics-live tabular" data-testid="diagnostics-live-phase">
          {latestPhase}
          {phaseDwellMs !== null ? ` ${formatDuration(phaseDwellMs)}` : ''}
        </p>
      ) : null}
    </div>
  )
}

/** The verdict headline derived from the trial's status. */
function verdictLabel(trial: TrialRow): string {
  switch (trial.status) {
    case 'succeeded':
      return 'passed'
    case 'failed':
      return 'failed'
    case 'cancelled':
      return 'cancelled'
    case 'interrupted':
    case 'retryable':
      return 'retrying'
    default:
      return 'in flight'
  }
}

function OverviewTab({ detail, now }: { detail: TrialDetail; now: number | undefined }): JSX.Element {
  const { trial, blockReason, latestPhase, phaseDwellMs } = detail
  const failed = trial.status === 'failed'
  const dwells = stageDwells(trial, now)
  // The retained sub-phase + its dwell (Story E), shown only for a running trial
  // that has a live phase. Null after a restart or for a non-running trial, in
  // which case the row is omitted and the plain status above stands alone.
  const phaseLine = trial.status === 'running' ? phaseDwellText(latestPhase, phaseDwellMs) : null
  return (
    <section className="trial-overview" data-testid="overview-tab">
      <Field label="verdict" value={verdictLabel(trial)} testId="trial-verdict" />
      <Field label="status" value={trial.status} testId="trial-status" />
      {phaseLine !== null ? <Field label="phase" value={phaseLine} testId="trial-phase" /> : null}
      <Field label="arm" value={trial.armId} />
      <Field label="scenario" value={trial.scenario} />
      <Field label="attempt" value={`${trial.attempt} / ${trial.maxAttempts}`} testId="trial-attempt" numeric />

      {failed ? (
        <div className="trial-failure" data-testid="trial-failure">
          <Field label="error class" value={trial.errorClass ?? 'unknown'} />
          <Field label="error message" value={trial.errorMessage ?? '—'} />
        </div>
      ) : null}

      {blockReason !== null ? <Field label="blocked" value={blockReason} testId="trial-block-reason" /> : null}

      <fieldset className="trial-section">
        <legend>lease</legend>
        <Field label="owner" value={trial.leaseOwner ?? '—'} testId="lease-owner" />
        <Field label="epoch" value={String(trial.leaseEpoch)} numeric />
        <Field label="expires" value={formatInstant(trial.leaseExpiresAt)} testId="lease-expires" numeric />
      </fieldset>

      <fieldset className="trial-section">
        <legend>timestamps &amp; dwell</legend>
        <TimestampRow label="created" ts={trial.createdAt} dwell={dwells[0]?.dwellMs ?? null} />
        <TimestampRow label="leased" ts={trial.leasedAt} dwell={dwells[1]?.dwellMs ?? null} />
        <TimestampRow label="started" ts={trial.startedAt} dwell={dwells[2]?.dwellMs ?? null} />
        <TimestampRow label="completed" ts={trial.completedAt} dwell={null} />
      </fieldset>
    </section>
  )
}

function Field({
  label,
  value,
  testId,
  numeric,
}: {
  label: string
  value: string
  testId?: string
  numeric?: boolean
}): JSX.Element {
  return (
    <div className="trial-field" data-testid={testId}>
      <span className="trial-field-label">{label}</span>
      <span className={numeric ? 'trial-field-value tabular' : 'trial-field-value'}>{value}</span>
    </div>
  )
}

/** A timestamp row carrying the absolute instant plus the per-stage dwell it opened. */
function TimestampRow({ label, ts, dwell }: { label: string; ts: number | null; dwell: number | null }): JSX.Element {
  return (
    <div className="trial-field" data-testid={`ts-${label}`}>
      <span className="trial-field-label">{label}</span>
      <span className="trial-field-value tabular">{formatInstant(ts)}</span>
      <span className="trial-dwell tabular">{dwell === null ? '' : formatDuration(dwell)}</span>
    </div>
  )
}
